function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


/* PonesHK desktop hover dropdown */
(() => {
  const initPonesHoverDropdown = () => {
    const desktopQuery = window.matchMedia('(min-width: 990px)');

    document.querySelectorAll('.header__inline-menu header-menu').forEach((menu) => {
      if (menu.dataset.ponesHoverReady === 'true') return;

      const details = menu.querySelector('details');
      const summary = details?.querySelector('summary');
      const submenu = details?.querySelector('.header__submenu');
      if (!details || !summary || !submenu) return;

      let closeTimer;
      const setExpanded = (expanded) => {
        details.open = expanded;
        summary.setAttribute('aria-expanded', String(expanded));
      };
      const openMenu = () => {
        if (!desktopQuery.matches) return;
        window.clearTimeout(closeTimer);
        setExpanded(true);
      };
      const scheduleClose = () => {
        if (!desktopQuery.matches) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (menu.matches(':hover') || submenu.matches(':hover')) return;
          setExpanded(false);
        }, 220);
      };

      menu.addEventListener('pointerenter', openMenu);
      menu.addEventListener('pointerleave', scheduleClose);
      submenu.addEventListener('pointerenter', openMenu);
      submenu.addEventListener('pointerleave', scheduleClose);
      desktopQuery.addEventListener('change', (event) => {
        if (!event.matches) setExpanded(false);
      });

      menu.dataset.ponesHoverReady = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPonesHoverDropdown, { once: true });
  } else {
    initPonesHoverDropdown();
  }
  document.addEventListener('shopify:section:load', initPonesHoverDropdown);
})();


/* PonesHK desktop product slider drag and progress control */
(() => {
  const sliderSelector = '#Slider-template--25748004503886__featured_collection';
  const sectionSelector = '#shopify-section-template--25748004503886__featured_collection';
  const dragThreshold = 8;

  const ensureStyles = () => {
    if (document.getElementById('pones-product-slider-styles')) return;
    const style = document.createElement('style');
    style.id = 'pones-product-slider-styles';
    style.textContent = `
      ${sectionSelector} .slider-buttons {
        display: grid !important;
        grid-template-columns: 44px minmax(180px, 520px) 44px;
        align-items: center;
        justify-content: center;
        gap: 14px;
        width: min(100%, 660px) !important;
        max-width: calc(100% - 3rem);
        height: 52px;
        margin: 2.4rem auto 0;
      }
      ${sectionSelector} .slider-counter { display: none !important; }
      ${sectionSelector} .slider-button {
        width: 44px;
        height: 44px;
        color: #1B97C2;
        border-radius: 50%;
        transition: color .2s ease, background .2s ease, opacity .2s ease;
      }
      ${sectionSelector} .slider-button:not([disabled]):hover {
        color: #137da4;
        background: rgba(27, 151, 194, .08);
      }
      ${sectionSelector} .slider-button[disabled] {
        color: #aab8bd;
        opacity: .42;
      }
      ${sectionSelector} .pones-product-progress {
        appearance: none;
        -webkit-appearance: none;
        width: 100%;
        height: 28px;
        margin: 0;
        background: transparent;
        cursor: pointer;
      }
      ${sectionSelector} .pones-product-progress::-webkit-slider-runnable-track {
        height: 5px;
        border-radius: 999px;
        background: #d7e5ea;
      }
      ${sectionSelector} .pones-product-progress::-webkit-slider-thumb {
        appearance: none;
        -webkit-appearance: none;
        width: 20px;
        height: 20px;
        margin-top: -7.5px;
        border: 0;
        border-radius: 50%;
        background: #1B97C2;
        box-shadow: 0 2px 8px rgba(13, 94, 123, .22);
        transition: background .2s ease, box-shadow .2s ease, transform .2s ease;
      }
      ${sectionSelector} .pones-product-progress:hover::-webkit-slider-thumb {
        background: #137da4;
        transform: scale(1.08);
      }
      ${sectionSelector} .pones-product-progress.is-active::-webkit-slider-thumb {
        background: #137da4;
        box-shadow: 0 0 0 8px rgba(27, 151, 194, .18), 0 2px 8px rgba(13, 94, 123, .24);
      }
      ${sectionSelector} .pones-product-progress::-moz-range-track {
        height: 5px;
        border: 0;
        border-radius: 999px;
        background: #d7e5ea;
      }
      ${sectionSelector} .pones-product-progress::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border: 0;
        border-radius: 50%;
        background: #1B97C2;
        box-shadow: 0 2px 8px rgba(13, 94, 123, .22);
      }
      ${sectionSelector} .quick-add__submit {
        --color-button: 27, 151, 194;
        --color-button-text: 255, 255, 255;
        background: #1B97C2 !important;
        color: #fff !important;
        border-color: #1B97C2 !important;
      }
      ${sectionSelector} .quick-add__submit:hover { background: #137da4 !important; }
      @media (max-width: 749px) {
        ${sectionSelector} .slider-buttons {
          grid-template-columns: 40px minmax(120px, 1fr) 40px;
          gap: 8px;
          max-width: calc(100% - 2rem);
        }
      }
    `;
    document.head.appendChild(style);
  };

  const initPonesProductDrag = () => {
    const slider = document.querySelector(sliderSelector);
    if (!slider || slider.dataset.ponesDragReady === 'true') return;

    ensureStyles();

    let pointerIsDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasMoved = false;
    let blockClick = false;

    const component = slider.closest('slider-component');
    const controls = component?.querySelector('.slider-buttons');
    const nextButton = controls?.querySelector('.slider-button--next');
    let progress = controls?.querySelector('.pones-product-progress');

    if (controls && nextButton && !progress) {
      progress = document.createElement('input');
      progress.type = 'range';
      progress.min = '0';
      progress.max = '100';
      progress.step = '0.1';
      progress.value = '0';
      progress.className = 'pones-product-progress';
      progress.setAttribute('aria-label', '瀏覽醫美精選產品');
      controls.insertBefore(progress, nextButton);
      progress.addEventListener('input', () => {
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        slider.scrollLeft = (Number(progress.value) / 100) * maxScroll;
      });
      progress.addEventListener('pointerdown', () => progress.classList.add('is-active'));
      ['pointerup', 'pointercancel', 'blur'].forEach((name) => {
        progress.addEventListener(name, () => progress.classList.remove('is-active'));
      });
    }

    const updateProgress = () => {
      if (!progress) return;
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      progress.value = maxScroll ? String((slider.scrollLeft / maxScroll) * 100) : '0';
      progress.disabled = maxScroll === 0;
    };

    slider.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });
    requestAnimationFrame(updateProgress);

    const finishDrag = (event) => {
      if (!pointerIsDown) return;
      pointerIsDown = false;
      slider.classList.remove('is-dragging');

      try {
        if (event?.pointerId != null && slider.hasPointerCapture(event.pointerId)) {
          slider.releasePointerCapture(event.pointerId);
        }
      } catch (error) {
        // Pointer capture may already have been released by the browser.
      }

      if (hasMoved) {
        blockClick = true;
        window.setTimeout(() => {
          blockClick = false;
        }, 0);
      }
    };

    slider.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.pointerType === 'touch') return;
      pointerIsDown = true;
      hasMoved = false;
      startX = event.clientX;
      startScrollLeft = slider.scrollLeft;
    });

    slider.addEventListener('pointermove', (event) => {
      if (!pointerIsDown) return;
      const distance = event.clientX - startX;

      if (!hasMoved && Math.abs(distance) > dragThreshold) {
        hasMoved = true;
        slider.classList.add('is-dragging');
        try {
          slider.setPointerCapture(event.pointerId);
        } catch (error) {
          // Continue dragging even when capture is unavailable.
        }
      }

      if (hasMoved) {
        event.preventDefault();
        slider.scrollLeft = startScrollLeft - distance;
      }
    });

    slider.addEventListener('pointerup', finishDrag);
    slider.addEventListener('pointercancel', finishDrag);
    slider.addEventListener('lostpointercapture', finishDrag);
    slider.addEventListener('pointerleave', () => {
      if (pointerIsDown && !hasMoved) pointerIsDown = false;
    });
    slider.addEventListener('dragstart', (event) => event.preventDefault());
    slider.addEventListener(
      'click',
      (event) => {
        if (!blockClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    slider.dataset.ponesDragReady = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPonesProductDrag, { once: true });
  } else {
    initPonesProductDrag();
  }

  document.addEventListener('shopify:section:load', initPonesProductDrag);
})();


/* PonesHK desktop slideshow drag */
(() => {
  const sliderSelector = '#Slider-template--25748004503886__slideshow_ErNk8M';

  const initPonesSlideshowDrag = () => {
    const slider = document.querySelector(sliderSelector);
    if (!slider || slider.dataset.ponesDragReady === 'true') return;

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasMoved = false;
    let blockClick = false;
    let resumeAutoPlay = false;
    let startSlideIndex = 0;
    let dragDirection = 0;

    const finishDrag = (event) => {
      if (!isDragging) return;
      isDragging = false;
      slider.classList.remove('is-dragging');

      try {
        if (event?.pointerId != null && slider.hasPointerCapture(event.pointerId)) {
          slider.releasePointerCapture(event.pointerId);
        }
      } catch (error) {
        // Pointer capture may already have been released by the browser.
      }

      if (hasMoved) {
        const slideWidth = slider.clientWidth;
        const slideCount = Math.max(1, Math.round(slider.scrollWidth / slideWidth));
        const step = dragDirection < 0 ? 1 : -1;
        const targetIndex = Math.min(slideCount - 1, Math.max(0, startSlideIndex + step));
        slider.scrollTo({ left: targetIndex * slideWidth, behavior: 'smooth' });
        blockClick = true;
        window.setTimeout(() => {
          blockClick = false;
        }, 0);
      }

      if (resumeAutoPlay) {
        const autoplayButton = slider
          .closest('slideshow-component')
          ?.querySelector('.slideshow__autoplay');
        window.setTimeout(() => {
          if (autoplayButton?.classList.contains('slideshow__autoplay--paused')) {
            autoplayButton.click();
          }
        }, 450);
        resumeAutoPlay = false;
      }
    };

    slider.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.pointerType === 'touch') return;

      const autoplayButton = slider
        .closest('slideshow-component')
        ?.querySelector('.slideshow__autoplay');
      if (autoplayButton && !autoplayButton.classList.contains('slideshow__autoplay--paused')) {
        autoplayButton.click();
        resumeAutoPlay = true;
      }

      isDragging = true;
      hasMoved = false;
      startX = event.clientX;
      startScrollLeft = slider.scrollLeft;
      startSlideIndex = Math.round(startScrollLeft / slider.clientWidth);
      dragDirection = 0;
    });

    slider.addEventListener('pointermove', (event) => {
      if (!isDragging) return;

      const distance = event.clientX - startX;
      dragDirection = Math.sign(distance);
      if (Math.abs(distance) > 8) {
        if (!hasMoved) {
          try {
            slider.setPointerCapture(event.pointerId);
          } catch (error) {
            // Continue dragging even when capture is unavailable.
          }
        }
        hasMoved = true;
        slider.classList.add('is-dragging');
        event.preventDefault();
        slider.scrollLeft = startScrollLeft - distance;
      }
    });

    slider.addEventListener('pointerup', finishDrag);
    slider.addEventListener('pointercancel', finishDrag);
    slider.addEventListener('lostpointercapture', finishDrag);
    slider.addEventListener('dragstart', (event) => event.preventDefault());
    slider.addEventListener(
      'click',
      (event) => {
        if (!blockClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    slider.dataset.ponesDragReady = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPonesSlideshowDrag, { once: true });
  } else {
    initPonesSlideshowDrag();
  }

  document.addEventListener('shopify:section:load', initPonesSlideshowDrag);
})();

/* PonesHK product thumbnail row: click-safe left-button drag */
(() => {
  const dragThreshold = 8;

  const initPonesThumbnailDrag = (scope = document) => {
    scope.querySelectorAll('media-gallery .thumbnail-list').forEach((list) => {
      if (list.dataset.ponesDragReady === 'true') return;

      let pointerIsDown = false;
      let hasMoved = false;
      let blockClick = false;
      let startX = 0;
      let startScrollLeft = 0;

      const finishDrag = (event) => {
        if (!pointerIsDown) return;
        pointerIsDown = false;
        list.classList.remove('is-dragging');

        try {
          if (event?.pointerId != null && list.hasPointerCapture(event.pointerId)) {
            list.releasePointerCapture(event.pointerId);
          }
        } catch (error) {
          // Pointer capture may already have been released by the browser.
        }

        if (hasMoved) {
          blockClick = true;
          window.setTimeout(() => {
            blockClick = false;
          }, 0);
        }
      };

      list.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.pointerType === 'touch') return;
        pointerIsDown = true;
        hasMoved = false;
        startX = event.clientX;
        startScrollLeft = list.scrollLeft;
      });

      list.addEventListener('pointermove', (event) => {
        if (!pointerIsDown) return;
        const distance = event.clientX - startX;

        if (!hasMoved && Math.abs(distance) > dragThreshold) {
          hasMoved = true;
          list.classList.add('is-dragging');
          try {
            list.setPointerCapture(event.pointerId);
          } catch (error) {
            // Continue dragging even when capture is unavailable.
          }
        }

        if (hasMoved) {
          event.preventDefault();
          list.scrollLeft = startScrollLeft - distance;
        }
      });

      list.addEventListener('pointerup', finishDrag);
      list.addEventListener('pointercancel', finishDrag);
      list.addEventListener('lostpointercapture', finishDrag);
      list.addEventListener('pointerleave', () => {
        if (pointerIsDown && !hasMoved) pointerIsDown = false;
      });
      list.addEventListener('dragstart', (event) => event.preventDefault());
      list.addEventListener(
        'click',
        (event) => {
          if (!blockClick) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );

      list.dataset.ponesDragReady = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPonesThumbnailDrag(), { once: true });
  } else {
    initPonesThumbnailDrag();
  }

  document.addEventListener('shopify:section:load', (event) => initPonesThumbnailDrag(event.target));
})();

/* PonesHK rotating, linked announcement messages */
(() => {
  const messages = [
    {
      text: '購物滿 HK$500 享免運費',
      href: '/pages/%E9%80%81%E8%B2%A8%E5%AE%89%E6%8E%92'
    },
    {
      text: 'dermaFIT 正式登陸香港・全線自動享75折｜毋須優惠碼',
      href: '/collections/dermafit-%E9%81%8B%E5%8B%95%E4%BF%AE%E8%AD%B7%E8%88%87%E8%BA%AB%E9%AB%94%E8%AD%B7%E7%90%86'
    },
  ];

  const initPonesAnnouncements = (scope = document) => {
    const bar = scope.querySelector?.('.announcement-bar') || document.querySelector('.announcement-bar');
    if (!bar || bar.dataset.ponesAnnouncementsReady === 'true') return;

    let index = 0;
    let timer = null;
    const link = document.createElement('a');
    link.className = 'pones-announcement-link';
    link.innerHTML = '<span class="pones-announcement-text"></span><span class="pones-announcement-arrow" aria-hidden="true">→</span>';
    bar.replaceChildren(link);

    const render = () => {
      link.classList.remove('is-visible');
      window.setTimeout(() => {
        const message = messages[index];
        link.href = message.href;
        link.querySelector('.pones-announcement-text').textContent = message.text;
        link.classList.add('is-visible');
      }, 120);
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      timer = window.setInterval(() => {
        index = (index + 1) % messages.length;
        render();
      }, 5000);
    };

    bar.addEventListener('pointerenter', stop);
    bar.addEventListener('pointerleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    render();
    start();
    bar.dataset.ponesAnnouncementsReady = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPonesAnnouncements(), { once: true });
  } else {
    initPonesAnnouncements();
  }

  document.addEventListener('shopify:section:load', (event) => initPonesAnnouncements(event.target));
})();

/* PonesHK floating WhatsApp contact */
(() => {
  const initPonesWhatsApp = () => {
    if (document.querySelector('.pones-whatsapp')) return;
    const link = document.createElement('a');
    link.className = 'pones-whatsapp';
    link.href = 'https://wa.me/85244894747';
    link.target = '_blank';
    link.rel = 'noopener';
    link.setAttribute('aria-label', 'WhatsApp 聯絡 PonesHK');
    link.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 11.6a8 8 0 0 1-11.8 7L4 20l1.4-4A8 8 0 1 1 20 11.6Z"/><path d="M8.8 8.3c.2-.4.4-.4.7-.4h.5l.7 1.8c.1.3.1.5-.1.7l-.6.7c-.2.2-.1.4 0 .6.6 1 1.4 1.8 2.4 2.3.2.1.4.1.6-.1l.8-1c.2-.2.4-.3.7-.2l1.8.9c.3.1.4.3.4.6 0 1-.5 1.7-1.2 2.1-.6.4-1.5.5-2.5.2-1.5-.5-3.2-1.4-4.7-2.9-1.2-1.2-2.1-2.7-2.4-4-.3-1 .1-1.8.5-2.3Z"/></svg>`;
    document.body.appendChild(link);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPonesWhatsApp, { once: true });
  } else {
    initPonesWhatsApp();
  }
})();
 ;(() => {   const initPonesFooter = () => {     const footer = document.querySelector('footer.footer');     if (!footer || footer.dataset.ponesFooterReady === 'true') return;      const newsletterForm = footer.querySelector('form.footer__newsletter');     const localization = footer.querySelector('.footer__localization');     const payment = footer.querySelector('.footer__payment');     const headerLogo = document.querySelector('img.header__heading-logo, .header__heading-link img');     const logoUrl = headerLogo ? (headerLogo.currentSrc || headerLogo.src) : '';     const shell = document.createElement('div');     shell.className = 'pones-footer-shell page-width';     shell.innerHTML = '<div class="pones-footer-grid"><div class="pones-footer-brand"><a href="/" aria-label="PonesHK 主頁" class="pones-footer-brand-link"></a><p>以專業嚴選品全球醫美級護膚產品，為顧客帶來安心、有效而值得信賴的護膚體驗。</p></div><details class="pones-footer-group" open><summary>產品連結</summary><ul class="pones-footer-links"><li><a href="/collections/retail-products">家用護膚產品</a></li><li><a href="/collections/professional-products">專業醫美產品</a></li><li><a href="/pages/about-us">關於我們</a></li><li><a href="/pages/contact">聯絡我們</a></li></ul></details><details class="pones-footer-group" open><summary>客戶服務</summary><ul class="pones-footer-links"><li><a href="/pages/%E9%80%81%E8%B2%A8%E5%AE%89%E6%8E%92">送貨安排</a></li><li><a href="/pages/returns-policy">退換政策</a></li><li><a href="/policies/privacy-policy">私隱政策</a></li><li><a href="/pages/%E6%9C%8D%E5%8B%99%E6%A2%9D%E6%AC%BE">服務條款</a></li></ul></details><div class="pones-footer-newsletter"><h2 class="pones-footer-title">訂閱電子報</h2><div class="pones-footer-newsletter-slot"></div><a class="pones-footer-instagram" href="https://www.instagram.com/pones_hongkong/" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5"></rect><circle cx="12" cy="12" r="4"></circle><circle cx="17.4" cy="6.7" r=".8" fill="currentColor" stroke="none"></circle></svg><span>Instagram</span></a></div></div><div class="pones-footer-bottom"><div class="pones-footer-bottom-left"><div class="pones-footer-localization-slot"></div></div><div class="pones-footer-bottom-right"><div class="pones-footer-payment-slot"></div><span class="pones-footer-copyright">© '+new Date().getFullYear()+' PonesHK</span></div></div>';      if (logoUrl) {       const logo = document.createElement('img');       logo.className = 'pones-footer-brand-logo';       logo.src = logoUrl;       logo.alt = 'PonesHK';       shell.querySelector('.pones-footer-brand-link').appendChild(logo);     } else {       shell.querySelector('.pones-footer-brand-link').textContent = 'PonesHK';     }     if (newsletterForm) shell.querySelector('.pones-footer-newsletter-slot').appendChild(newsletterForm);     if (localization) shell.querySelector('.pones-footer-localization-slot').appendChild(localization);     if (payment) shell.querySelector('.pones-footer-payment-slot').appendChild(payment);      footer.replaceChildren(shell);     footer.classList.add('pones-footer-ready');     footer.dataset.ponesFooterReady = 'true';      const groups = [...footer.querySelectorAll('.pones-footer-group')];     const mobile = window.matchMedia('(max-width: 749px)');     const syncGroups = () => groups.forEach((group) => { group.open = !mobile.matches; });     syncGroups();     mobile.addEventListener?.('change', syncGroups);   };    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPonesFooter, { once: true });   else initPonesFooter();   document.addEventListener('shopify:section:load', initPonesFooter); })(); 
;(()=>{const syncPonesFinalLabels=()=>{const footerTitle=document.querySelector('.pones-footer-title');if(footerTitle)footerTitle.textContent='訂閱優惠資訊';document.querySelectorAll('#shopify-section-template--25748004503886__slideshow_ErNk8M .slideshow__slide a.button').forEach((link)=>{if(link.textContent.trim()==='Buy now')link.textContent='了解產品';});};if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',syncPonesFinalLabels,{once:true});}else{syncPonesFinalLabels();}document.addEventListener('shopify:section:load',syncPonesFinalLabels);})();

;(()=>{const syncPonesFinalLabels=()=>{const footerTitle=document.querySelector('.pones-footer-title');if(footerTitle)footerTitle.textContent='訂閱優惠資訊';document.querySelectorAll('#shopify-section-template--25748004503886__slideshow_ErNk8M .slideshow__slide a.button').forEach((link)=>{if(link.textContent.trim()==='Buy now')link.textContent='了解產品';});};if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',syncPonesFinalLabels,{once:true});}else{syncPonesFinalLabels();}document.addEventListener('shopify:section:load',syncPonesFinalLabels);})();

;(()=>{const ponesManualAnnouncements=[{text:'購物滿 HK$500 享免運費',href:'/pages/%E9%80%81%E8%B2%A8%E5%AE%89%E6%8E%92'},{text:'dermaFIT 正式登陸香港・全線自動享75折｜毋須優惠碼',href:'/collections/dermafit-%E9%81%8B%E5%8B%95%E4%BF%AE%E8%AD%B7%E8%88%87%E8%BA%AB%E9%AB%94%E8%AD%B7%E7%90%86'}];const initPonesAnnouncementNav=(root=document)=>{root.querySelectorAll('.announcement-bar[data-pones-announcements-ready=\"true\"]').forEach((bar)=>{if(bar.dataset.ponesManualNavReady==='true')return;const link=bar.querySelector('.pones-announcement-link');const text=link?.querySelector('.pones-announcement-text');if(!link||!text)return;const makeButton=(direction,label,symbol)=>{const button=document.createElement('button');button.type='button';button.className='pones-announcement-nav pones-announcement-nav--'+direction;button.setAttribute('aria-label',label);button.textContent=symbol;button.addEventListener('click',()=>{const currentText=text.textContent.trim();let index=ponesManualAnnouncements.findIndex((item)=>item.text===currentText);if(index<0)index=0;index=(index+(direction==='next'?1:-1)+ponesManualAnnouncements.length)%ponesManualAnnouncements.length;const item=ponesManualAnnouncements[index];link.classList.add('is-switching');setTimeout(()=>{text.textContent=item.text;link.href=item.href;setTimeout(()=>link.classList.remove('is-switching'),30);},180);});return button;};bar.prepend(makeButton('prev','上一則訊息','←'));bar.append(makeButton('next','下一則訊息','→'));bar.dataset.ponesManualNavReady='true';});};if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',()=>initPonesAnnouncementNav(),{once:true});}else{initPonesAnnouncementNav();}document.addEventListener('shopify:section:load',(event)=>initPonesAnnouncementNav(event.target));})();

;(()=>{const shippingMessage='購物滿 HK$500 享免運費';const syncPonesAnnouncementLink=()=>{const link=document.querySelector('.pones-announcement-link');const text=link?.querySelector('.pones-announcement-text');if(!link||!text)return;link.querySelector('.pones-announcement-arrow')?.remove();if(text.textContent.trim()===shippingMessage){link.removeAttribute('href');link.classList.add('is-static');link.setAttribute('aria-disabled','true');}else{link.classList.remove('is-static');link.removeAttribute('aria-disabled');}};const observePonesAnnouncement=()=>{const text=document.querySelector('.pones-announcement-text');if(!text||text.dataset.ponesLinkObserverReady==='true')return;syncPonesAnnouncementLink();new MutationObserver(syncPonesAnnouncementLink).observe(text,{childList:true,characterData:true,subtree:true});text.dataset.ponesLinkObserverReady='true';};if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',observePonesAnnouncement,{once:true});}else{observePonesAnnouncement();}document.addEventListener('shopify:section:load',observePonesAnnouncement);})();


/* PonesHK product card quick-add isolation */
(() => {
  const selector = '.product-card-wrapper .quick-add';

  const bindQuickAddZones = (root = document) => {
    const zones = [];

    if (root instanceof Element && root.matches(selector)) zones.push(root);
    if (root.querySelectorAll) zones.push(...root.querySelectorAll(selector));

    zones.forEach((zone) => {
      if (zone.dataset.ponesQuickAddIsolated === 'true') return;

      ['pointerdown', 'pointerup', 'mousedown', 'click'].forEach((eventName) => {
        zone.addEventListener(eventName, (event) => event.stopPropagation());
      });

      zone.dataset.ponesQuickAddIsolated = 'true';
    });
  };

  const initQuickAddIsolation = () => {
    bindQuickAddZones();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) bindQuickAddZones(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickAddIsolation, { once: true });
  } else {
    initQuickAddIsolation();
  }

  document.addEventListener('shopify:section:load', (event) => bindQuickAddZones(event.target));
})();


/* PonesHK product lightbox carousel */
(()=>{const initPonesLightboxes=(root=document)=>{root.querySelectorAll("product-modal.product-media-modal").forEach((modal)=>{if(modal.dataset.ponesLightboxReady==="true")return;const dialog=modal.querySelector(".product-media-modal__dialog");const content=modal.querySelector(".product-media-modal__content");if(!dialog||!content)return;const items=()=>[...content.children].filter((item)=>!item.matches("button"));let index=0;const sync=()=>{const list=items();if(!list.length)return;const center=content.scrollLeft+content.clientWidth/2;let best=0,bestDistance=Infinity;list.forEach((item,i)=>{const distance=Math.abs(item.offsetLeft+item.offsetWidth/2-center);if(distance<bestDistance){best=i;bestDistance=distance;}});index=best;prev.disabled=index===0;next.disabled=index===list.length-1;};const go=(step)=>{const list=items();if(!list.length)return;sync();index=Math.max(0,Math.min(list.length-1,index+step));content.scrollTo({left:list[index].offsetLeft,behavior:"smooth"});setTimeout(sync,260);};const make=(direction,label,step)=>{const button=document.createElement("button");button.type="button";button.className="pones-lightbox-nav pones-lightbox-nav--"+direction;button.setAttribute("aria-label",label);["pointerdown","pointerup","mousedown"].forEach((name)=>button.addEventListener(name,(event)=>event.stopPropagation(),true));button.addEventListener("click",(event)=>{event.preventDefault();event.stopImmediatePropagation();go(step);},true);return button;};const prev=make("prev","上一張產品圖片",-1);const next=make("next","下一張產品圖片",1);dialog.append(prev,next);let frame=0;content.addEventListener("scroll",()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(sync);},{passive:true});new MutationObserver(()=>{if(modal.hasAttribute("open"))setTimeout(sync,60);}).observe(modal,{attributes:true,attributeFilter:["open"]});modal.addEventListener("keydown",(event)=>{if(event.key==="ArrowLeft"){event.preventDefault();go(-1);}if(event.key==="ArrowRight"){event.preventDefault();go(1);}});modal.dataset.ponesLightboxReady="true";sync();});};if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",()=>initPonesLightboxes(),{once:true});else initPonesLightboxes();document.addEventListener("shopify:section:load",(event)=>initPonesLightboxes(event.target));})();
